import { MbeClose } from '@/components/icons';
import {
  Button,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Radio,
  RadioGroup,
} from '@chakra-ui/react';
import { FormProvider, useController, useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import type { IAddressBook, IBaseForm } from '@/types';
import { useTranslation } from 'react-i18next';
import { AddressFields, CtaLoader, MbeInput } from '@/components/shared';
import { hasFormAnyError } from '@/utils';
import { v4 as uuidv4 } from 'uuid';
import { emailRegex, ERRORS, phoneRegex } from '@/constants';

interface ISettingsAddressBookModalProps extends IBaseForm<IAddressBook> {
  isLoading?: boolean;
}

export const SettingsAddAddressBookModal: React.FC<ISettingsAddressBookModalProps> = ({
  isLoading,
  onClick: providedOnSubmit,
}): JSX.Element => {
  const { t } = useTranslation();

  //TODO[TYPES-ID] Add uuid type for id in IAddressBook

  const addressId: string = uuidv4();

  const methods = useForm<IAddressBook>({
    mode: 'all',
    criteriaMode: 'all',
    defaultValues: { isPrivateHome: true },
  });

  const {
    control,
    formState: { errors },
    handleSubmit: submitHandler,
    register,
    watch,
  } = methods;

  const { field: isPrivateHomeField } = useController({
    name: 'isPrivateHome',
    control,
  });

  const isPrivateHome = Boolean(watch('isPrivateHome'));

  const isDisabled = hasFormAnyError(errors);

  const submitNewPhoneNumberHandler: SubmitHandler<IAddressBook> = (newAddress, event): void => {
    event?.preventDefault();
    const submittedAddress: IAddressBook = { ...newAddress, id: addressId };
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call
    providedOnSubmit?.(submittedAddress);
  };

  const modalContainer: HTMLDivElement | null = document.querySelector(
    '.chakra-modal__content-container'
  );

  if (modalContainer) {
    modalContainer.style.zIndex = '999';
  }

  const ctaButtonContent = isLoading ? <CtaLoader /> : t('ACCOUNT.SETTINGS.CONFIRM');

  return (
    <>
      <ModalOverlay backdropFilter="blur(10px) hue-rotate(90deg)" bg="grey" zIndex="998" />
      <ModalContent
        borderRadius={0}
        minH={{ base: '100%', md: '80%' }}
        minW={{ base: 'full', md: '50%' }}
        my={{ base: 0, md: '3.75rem' }}
      >
        <ModalBody
          overflow="auto"
          p={0}
          paddingInlineEnd={0}
          paddingInlineStart={0}
          position="relative"
        >
          <ModalCloseButton
            _hover={{ bgColor: 'white' }}
            borderRadius="full"
            h={10}
            position="absolute"
            right="5%"
            top="15px"
            w={10}
            zIndex={3}
          >
            <MbeClose h="3rem" w="3rem" />
          </ModalCloseButton>
          <Flex
            alignItems="center"
            direction="column"
            h="full"
            justifyContent={{ base: 'flex-start', md: 'center' }}
            position={{ base: 'absolute' }}
            pt={{ base: 16, md: 0 }}
            px={{ base: '5%', md: '0' }}
            w="full"
            zIndex={2}
          >
            <ModalHeader fontSize={{ base: '22px', md: '32px' }} fontWeight="normal" mb={8}>
              {t('ACCOUNT.SETTINGS.MODIFY_ADDRESS_BOOK.ADD_ADDRESS')}
            </ModalHeader>
            <FormProvider {...methods}>
              <form autoComplete="off" id="addAddressForm" name="addAddressForm">
                <Flex alignItems="center" direction="column" gap={8} justifyContent="space-between">
                  <MbeInput
                    placeholder={t('ACCOUNT.SETTINGS.MODIFY_ADDRESS_BOOK.NEW_RECIPIENT')}
                    {...register('name')}
                  />
                  <AddressFields />
                  <MbeInput
                    error={errors.email}
                    id="email"
                    placeholder={t('FORM.RECIPIENT_EMAIL')}
                    {...register('email', {
                      pattern: { value: emailRegex, message: t(ERRORS.email) },
                    })}
                  />
                  <MbeInput
                    error={errors.phone}
                    id="phone"
                    placeholder={t('FORM.RECIPIENT_PHONE')}
                    {...register('phone', {
                      pattern: { value: phoneRegex, message: t(ERRORS.phoneNumber) },
                    })}
                  />
                  <RadioGroup
                    name="isPrivateHome"
                    value={isPrivateHome.toString()}
                    onChange={(nextVal): void => {
                      isPrivateHomeField.onChange(Boolean(nextVal === 'true'));
                    }}
                  >
                    <Flex gap={8}>
                      <Radio spacing={4} value="true" variant="mbe">
                        {t('FORM.ADDRESS_TYPE_HOME')}
                      </Radio>
                      <Radio spacing={4} value="false" variant="mbe">
                        {t('FORM.ADDRESS_TYPE_BUSINESS')}
                      </Radio>
                    </Flex>
                  </RadioGroup>
                  <Button
                    disabled={isDisabled}
                    mb={5}
                    variant="mbePrimary"
                    w="100%"
                    // eslint-disable-next-line @typescript-eslint/no-misused-promises
                    onClick={submitHandler(submitNewPhoneNumberHandler)}
                  >
                    {ctaButtonContent}
                  </Button>
                </Flex>
              </form>
            </FormProvider>
          </Flex>
        </ModalBody>
      </ModalContent>
    </>
  );
};
